import { startTransition, useDeferredValue, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ArrowLeft, ArrowRight, BarChart3, ChevronDown, ChevronRight, Download, FolderKanban, Lock, PanelLeft, RefreshCw, Search } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { PageHelp } from '../../components/PageHelp';
import { AccountLedgerDialog } from '../../components/financials/AccountLedgerDialog';
import { TableFilterBar } from '../../components/table-filters/TableFilterBar';
import { Button } from '../../components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { useTableFilterState } from '../../hooks/useTableFilterState';
import { cn } from '../../lib/utils';
import { api } from '../../services/api';
import type {
    Account,
    ContributionMarginRow,
    BreakEvenReport,
    BalanceSheetRow,
    EnquiryRegisterRow,
    EstimateCostBreakdownRow,
    EstimatedVsActualRow,
    GrcMaterialConsumptionRow,
    JobProductionCostRow,
    MaterialConsumptionRow,
    MinStockAlertRow,
    OwnerDashboardReport,
    PayablesAgingResponse,
    ProductionOutputResponse,
    ProductProfitabilityRow,
    ProfitAndLossReportLine,
    ProfitAndLossReport,
    QuotationRegisterRow,
    ReceivablesAgingResponse,
    ReceivablesAgingRow,
    ReportReleaseReadinessItem,
    ReportWorkspaceState,
    StockMove,
    StockValuationRecord,
    SupplierAgingSummaryRow,
    TrialBalanceReportResponse,
    TrialBalanceReportRow,
    VATSummary,
    VATSummaryRow,
    WipReportRow,
} from '../../services/types';
import { FinancialStatementTable } from './FinancialStatementTable';
import { PagedReportTable } from './PagedReportTable';
import { reportDefinitions, reportModules, type ReportDefinition, type ReportModule } from './reportRegistry';
import { VatFilingWorkbench, type VatWorkspaceMode } from './VatFilingWorkbench';

type TableRow =
    | EnquiryRegisterRow
    | QuotationRegisterRow
    | EstimateCostBreakdownRow
    | ProductionOutputResponse['items'][number]
    | StockValuationRecord
    | MinStockAlertRow
    | ReceivablesAgingRow
    | SupplierAgingSummaryRow
    | JobProductionCostRow
    | MaterialConsumptionRow
    | EstimatedVsActualRow
    | ContributionMarginRow
    | WipReportRow
    | ProductProfitabilityRow
    | GrcMaterialConsumptionRow
    | StockMove
    | ProfitAndLossReportLine
    | BalanceSheetRow
    | TrialBalanceReportRow
    | VATSummaryRow
    | MetricReportRow;

type MetricReportRow = {
    row_id: string;
    metric: string;
    value: string | number;
};

type ReportColumn = {
    header: string;
    accessor: (item: TableRow) => ReactNode;
    className?: string;
};

type LoadedReport =
    | { kind: 'table'; rows: TableRow[]; title: string; total: number; limit: number; offset: number; hasMore: boolean; summary?: Record<string, string | number>; note?: string; vat?: VATSummary }
    | { kind: 'linked'; title: string; note: string; targetRoute: string }
    | { kind: 'none'; title: string; note: string };

const badgeClass: Record<string, string> = {
    LIVE: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    IN_IMPLEMENTATION: 'border-amber-200 bg-amber-50 text-amber-700',
    BLOCKED: 'border-rose-200 bg-rose-50 text-rose-700',
};

const readinessBadgeClass: Record<string, string> = {
    ready_for_release: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    pending_approval: 'border-amber-200 bg-amber-50 text-amber-700',
    setup_incomplete: 'border-rose-200 bg-rose-50 text-rose-700',
};

const workspaceDefaults: Omit<ReportWorkspaceState, 'report'> = {
    q: '',
    range: '',
    group_by: 'daily',
    limit: '25',
    offset: '0',
};

function formatMoney(value: number | null | undefined) {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value || 0));
}

function formatMaybeNumber(value: number | null | undefined, digits = 2) {
    return Number(value || 0).toFixed(digits);
}

function toDateRange(range: string): { from?: string; to?: string } {
    const [from, to] = String(range || '').split('|');
    return {
        from: from || undefined,
        to: to || undefined,
    };
}

function toIsoDate(value: Date) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export function getLatestCompletedQuarterPeriod(now = new Date()) {
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    const start = new Date(end.getFullYear(), end.getMonth() - 2, 1);
    return { from: toIsoDate(start), to: toIsoDate(end) };
}

function formatPeriodDate(value: string) {
    const [year, month, day] = value.split('-').map(Number);
    return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    }).format(new Date(year, month - 1, day));
}

function getTableColumns(reportId: string) {
    switch (reportId) {
        case 'enquiry-register':
            return [
                { header: 'Inquiry', accessor: (row: EnquiryRegisterRow) => `#${row.inquiry_id}` },
                { header: 'Date', accessor: (row: EnquiryRegisterRow) => row.date || '-' },
                { header: 'Client', accessor: (row: EnquiryRegisterRow) => row.client || '-' },
                { header: 'Product', accessor: (row: EnquiryRegisterRow) => row.product_type || '-' },
                { header: 'Est. Value', accessor: (row: EnquiryRegisterRow) => row.estimated_value == null ? '-' : formatMoney(row.estimated_value), className: 'text-right' },
                { header: 'Status', accessor: (row: EnquiryRegisterRow) => row.status },
            ];
        case 'quotation-register':
            return [
                { header: 'Quotation', accessor: (row: QuotationRegisterRow) => row.quotation_number },
                { header: 'Date', accessor: (row: QuotationRegisterRow) => row.date || '-' },
                { header: 'Client', accessor: (row: QuotationRegisterRow) => row.client || '-' },
                { header: 'Value', accessor: (row: QuotationRegisterRow) => formatMoney(row.value), className: 'text-right' },
                { header: 'Status', accessor: (row: QuotationRegisterRow) => row.status },
            ];
        case 'estimated-cost-breakdown':
            return [
                { header: 'Estimate', accessor: (row: EstimateCostBreakdownRow) => `#${row.estimate_id}` },
                { header: 'Date', accessor: (row: EstimateCostBreakdownRow) => row.date || '-' },
                { header: 'Client', accessor: (row: EstimateCostBreakdownRow) => row.client || '-' },
                { header: 'Material', accessor: (row: EstimateCostBreakdownRow) => formatMoney(row.material_cost), className: 'text-right' },
                { header: 'Labour', accessor: (row: EstimateCostBreakdownRow) => formatMoney(row.labour_cost), className: 'text-right' },
                { header: 'Install', accessor: (row: EstimateCostBreakdownRow) => formatMoney(row.installation_cost), className: 'text-right' },
                { header: 'OH', accessor: (row: EstimateCostBreakdownRow) => formatMoney(row.overhead_cost), className: 'text-right' },
                { header: 'Margin', accessor: (row: EstimateCostBreakdownRow) => formatMoney(row.margin), className: 'text-right' },
            ];
        case 'production-output':
            return [
                { header: 'Bucket', accessor: (row: ProductionOutputResponse['items'][number]) => row.date || '-' },
                { header: 'Job', accessor: (row: ProductionOutputResponse['items'][number]) => row.job_no },
                { header: 'Product', accessor: (row: ProductionOutputResponse['items'][number]) => row.product || '-' },
                { header: 'Qty', accessor: (row: ProductionOutputResponse['items'][number]) => formatMaybeNumber(row.output_qty, 3), className: 'text-right' },
                { header: 'Weight (kg)', accessor: (row: ProductionOutputResponse['items'][number]) => formatMaybeNumber(row.output_weight_kgs, 3), className: 'text-right' },
            ];
        case 'raw-material-stock':
            return [
                { header: 'Code', accessor: (row: StockValuationRecord) => row.code || '-' },
                { header: 'Item', accessor: (row: StockValuationRecord) => row.name || '-' },
                { header: 'Qty', accessor: (row: StockValuationRecord) => formatMaybeNumber(row.quantity, 3), className: 'text-right' },
                { header: 'Value', accessor: (row: StockValuationRecord) => formatMoney(row.total_value), className: 'text-right' },
            ];
        default:
            return [];
    }
}

function getExtendedColumns(reportId: string) {
    if (['sales-pipeline', 'conversion-ratio', 'cash-flow', 'break-even', 'owner-dashboard'].includes(reportId)) {
        return [
            { header: 'Metric', accessor: (row: MetricReportRow) => row.metric },
            { header: 'Value', accessor: (row: MetricReportRow) => row.value, className: 'text-right' },
        ];
    }
    if (reportId === 'vat-summary') {
        return [
            { header: 'Type', accessor: (row: VATSummaryRow) => row.source_type },
            { header: 'Date', accessor: (row: VATSummaryRow) => row.document_date || '-' },
            { header: 'Document', accessor: (row: VATSummaryRow) => row.document_no || `#${row.document_id}` },
            { header: 'Party', accessor: (row: VATSummaryRow) => row.party_name || '-' },
            { header: 'Taxable', accessor: (row: VATSummaryRow) => formatMoney(row.taxable_amount), className: 'text-right' },
            { header: 'VAT %', accessor: (row: VATSummaryRow) => formatMaybeNumber(row.vat_percent, 2), className: 'text-right' },
            { header: 'VAT', accessor: (row: VATSummaryRow) => formatMoney(row.vat_amount), className: 'text-right' },
            { header: 'Grand Total', accessor: (row: VATSummaryRow) => formatMoney(row.grand_total), className: 'text-right' },
        ];
    }
    if (reportId === 'minimum-stock-alert') {
        return [
            { header: 'Code', accessor: (row: MinStockAlertRow) => row.code || '-' },
            { header: 'Item', accessor: (row: MinStockAlertRow) => row.name || '-' },
            { header: 'Current', accessor: (row: MinStockAlertRow) => formatMaybeNumber(row.current_stock, 3), className: 'text-right' },
            { header: 'Minimum', accessor: (row: MinStockAlertRow) => row.minimum_stock == null ? '-' : formatMaybeNumber(row.minimum_stock, 3), className: 'text-right' },
        ];
    }
    if (reportId === 'receivable-aging') {
        return [
            { header: 'Customer', accessor: (row: ReceivablesAgingRow) => row.customer_name || '-' },
            { header: '0-30', accessor: (row: ReceivablesAgingRow) => formatMoney(row.bucket_0_30), className: 'text-right' },
            { header: '31-60', accessor: (row: ReceivablesAgingRow) => formatMoney(row.bucket_31_60), className: 'text-right' },
            { header: '61-90', accessor: (row: ReceivablesAgingRow) => formatMoney(row.bucket_61_90), className: 'text-right' },
            { header: '90+', accessor: (row: ReceivablesAgingRow) => formatMoney(row.bucket_90_plus), className: 'text-right' },
            { header: 'Total', accessor: (row: ReceivablesAgingRow) => formatMoney(row.total_outstanding), className: 'text-right' },
        ];
    }
    if (reportId === 'payable-aging') {
        return [
            { header: 'Supplier', accessor: (row: SupplierAgingSummaryRow) => row.supplier_name || '-' },
            { header: '0-30', accessor: (row: SupplierAgingSummaryRow) => formatMoney(row.bucket_0_30), className: 'text-right' },
            { header: '31-60', accessor: (row: SupplierAgingSummaryRow) => formatMoney(row.bucket_31_60), className: 'text-right' },
            { header: '61-90', accessor: (row: SupplierAgingSummaryRow) => formatMoney(row.bucket_61_90), className: 'text-right' },
            { header: '90+', accessor: (row: SupplierAgingSummaryRow) => formatMoney(row.bucket_90_plus), className: 'text-right' },
            { header: 'Total', accessor: (row: SupplierAgingSummaryRow) => formatMoney(row.total_outstanding), className: 'text-right' },
        ];
    }
    if (reportId === 'job-production-cost') {
        return [
            { header: 'Job', accessor: (row: JobProductionCostRow) => row.job_no },
            { header: 'Product', accessor: (row: JobProductionCostRow) => row.product || '-' },
            { header: 'Qty', accessor: (row: JobProductionCostRow) => formatMaybeNumber(row.quantity, 3), className: 'text-right' },
            { header: 'Weight', accessor: (row: JobProductionCostRow) => formatMaybeNumber(row.weight_produced, 3), className: 'text-right' },
            { header: 'Material', accessor: (row: JobProductionCostRow) => formatMoney(row.material_issued_cost), className: 'text-right' },
            { header: 'Prod Hrs', accessor: (row: JobProductionCostRow) => formatMaybeNumber(row.labor_hours, 3), className: 'text-right' },
            { header: 'Inst Hrs', accessor: (row: JobProductionCostRow) => formatMaybeNumber(row.installation_hours, 3), className: 'text-right' },
            { header: 'Total Cost', accessor: (row: JobProductionCostRow) => formatMoney(row.total_production_cost), className: 'text-right' },
        ];
    }
    if (reportId === 'material-consumption-per-job') {
        return [
            { header: 'Job', accessor: (row: MaterialConsumptionRow) => row.job_no || '-' },
            { header: 'Item', accessor: (row: MaterialConsumptionRow) => row.item_name || '-' },
            { header: 'Qty', accessor: (row: MaterialConsumptionRow) => formatMaybeNumber(row.qty_consumed, 3), className: 'text-right' },
            { header: 'Cost', accessor: (row: MaterialConsumptionRow) => formatMoney(row.total_cost), className: 'text-right' },
            { header: 'Last Entry', accessor: (row: MaterialConsumptionRow) => row.last_entry_date || '-' },
        ];
    }
    if (reportId === 'estimated-vs-actual') {
        return [
            { header: 'Job', accessor: (row: EstimatedVsActualRow) => row.job_no },
            { header: 'Estimate', accessor: (row: EstimatedVsActualRow) => row.estimate_id ? `#${row.estimate_id}` : '-' },
            { header: 'Estimated', accessor: (row: EstimatedVsActualRow) => formatMoney(row.estimated_cost), className: 'text-right' },
            { header: 'Actual', accessor: (row: EstimatedVsActualRow) => formatMoney(row.actual_cost), className: 'text-right' },
            { header: 'Variance', accessor: (row: EstimatedVsActualRow) => formatMoney(row.variance), className: 'text-right' },
        ];
    }
    if (reportId === 'contribution-margin-per-job') {
        return [
            { header: 'Job', accessor: (row: ContributionMarginRow) => row.job },
            { header: 'Sales', accessor: (row: ContributionMarginRow) => formatMoney(row.sales), className: 'text-right' },
            { header: 'Prod Cost', accessor: (row: ContributionMarginRow) => formatMoney(row.production_cost), className: 'text-right' },
            { header: 'Margin', accessor: (row: ContributionMarginRow) => formatMoney(row.margin), className: 'text-right' },
        ];
    }
    if (reportId === 'stock-ledger') {
        return [
            { header: 'Date', accessor: (row: StockMove) => row.transaction_date || '-' },
            { header: 'Item', accessor: (row: StockMove) => row.item_code || '-' },
            { header: 'Description', accessor: (row: StockMove) => row.item_name || '-' },
            { header: 'Type', accessor: (row: StockMove) => row.transaction_type || '-' },
            { header: 'Qty', accessor: (row: StockMove) => formatMaybeNumber(row.quantity, 3), className: 'text-right' },
            { header: 'Rate', accessor: (row: StockMove) => formatMoney(row.unit_price), className: 'text-right' },
            { header: 'Value', accessor: (row: StockMove) => formatMoney(row.calculated_cost), className: 'text-right' },
            { header: 'Reference', accessor: (row: StockMove) => row.reference_doc || '-' },
        ];
    }
    if (reportId === 'balance-sheet') {
        return [
            { header: 'Section', accessor: (row: BalanceSheetRow) => row.section },
            { header: 'Code', accessor: (row: BalanceSheetRow) => row.account_code || '-' },
            { header: 'Account', accessor: (row: BalanceSheetRow) => row.account_name },
            { header: 'Type', accessor: (row: BalanceSheetRow) => row.row_type === 'section_total' ? 'Total' : 'Account' },
            { header: 'Balance', accessor: (row: BalanceSheetRow) => formatMoney(row.balance), className: 'text-right' },
        ];
    }
    if (reportId === 'trial-balance') {
        return [
            { header: 'Code', accessor: (row: TrialBalanceReportRow) => row.account_code || '-' },
            { header: 'Account Name', accessor: (row: TrialBalanceReportRow) => row.account_name || '-' },
            { header: 'Type', accessor: (row: TrialBalanceReportRow) => row.account_type || '-' },
            { header: 'Debit', accessor: (row: TrialBalanceReportRow) => formatMoney(row.debit), className: 'text-right' },
            { header: 'Credit', accessor: (row: TrialBalanceReportRow) => formatMoney(row.credit), className: 'text-right' },
        ];
    }
    if (reportId === 'profit-and-loss') {
        return [
            { header: 'Section', accessor: (row: ProfitAndLossReportLine) => row.section },
            { header: 'Code', accessor: (row: ProfitAndLossReportLine) => row.account_code || '-' },
            { header: 'Account', accessor: (row: ProfitAndLossReportLine) => row.account_name || row.name },
            { header: 'Type', accessor: (row: ProfitAndLossReportLine) => row.row_type === 'section_total' ? 'Total' : 'Account' },
            { header: 'Amount', accessor: (row: ProfitAndLossReportLine) => formatMoney(row.balance), className: 'text-right' },
        ];
    }
    if (reportId === 'wip-report') {
        return [
            { header: 'Job', accessor: (row: WipReportRow) => row.job_no },
            { header: 'Customer', accessor: (row: WipReportRow) => row.customer || '-' },
            { header: 'Status', accessor: (row: WipReportRow) => row.status },
            { header: 'Target Qty', accessor: (row: WipReportRow) => formatMaybeNumber(row.target_qty, 3), className: 'text-right' },
            { header: 'Produced Qty', accessor: (row: WipReportRow) => formatMaybeNumber(row.produced_qty, 3), className: 'text-right' },
            { header: 'Complete %', accessor: (row: WipReportRow) => `${row.completion_percent.toFixed(2)}%`, className: 'text-right' },
            { header: 'WIP Value', accessor: (row: WipReportRow) => formatMoney(row.wip_value), className: 'text-right' },
            { header: 'Validation', accessor: (row: WipReportRow) => row.validation_status || 'OK' },
        ];
    }
    if (reportId === 'product-profitability') {
        return [
            { header: 'Family', accessor: (row: ProductProfitabilityRow) => row.family },
            { header: 'Revenue', accessor: (row: ProductProfitabilityRow) => formatMoney(row.revenue), className: 'text-right' },
            { header: 'Actual Cost', accessor: (row: ProductProfitabilityRow) => formatMoney(row.actual_cost), className: 'text-right' },
            { header: 'Profit', accessor: (row: ProductProfitabilityRow) => formatMoney(row.profit), className: 'text-right' },
            { header: 'Margin %', accessor: (row: ProductProfitabilityRow) => `${row.profit_margin_percent.toFixed(2)}%`, className: 'text-right' },
            { header: 'Invoices', accessor: (row: ProductProfitabilityRow) => row.invoice_count, className: 'text-right' },
            { header: 'Jobs', accessor: (row: ProductProfitabilityRow) => row.job_count, className: 'text-right' },
        ];
    }
    if (reportId === 'grc-material-consumption') {
        return [
            { header: 'Job', accessor: (row: GrcMaterialConsumptionRow) => row.job_no },
            { header: 'Bucket', accessor: (row: GrcMaterialConsumptionRow) => row.bucket },
            { header: 'Item', accessor: (row: GrcMaterialConsumptionRow) => row.item_name || row.item_code || '-' },
            { header: 'Qty', accessor: (row: GrcMaterialConsumptionRow) => formatMaybeNumber(row.qty_consumed, 3), className: 'text-right' },
            { header: 'Cost', accessor: (row: GrcMaterialConsumptionRow) => formatMoney(row.total_cost), className: 'text-right' },
            { header: 'Output Wt (kg)', accessor: (row: GrcMaterialConsumptionRow) => formatMaybeNumber(row.output_weight_kgs, 3), className: 'text-right' },
            { header: 'Last Entry', accessor: (row: GrcMaterialConsumptionRow) => row.last_entry_date || '-' },
        ];
    }
    return null;
}

function getTableColumnsForReport(reportId: string) {
    return getExtendedColumns(reportId) || getTableColumns(reportId);
}

function getKeyField(reportId: string): keyof TableRow {
    if (['sales-pipeline', 'conversion-ratio', 'cash-flow', 'break-even', 'owner-dashboard', 'vat-summary'].includes(reportId)) return 'row_id' as keyof TableRow;
    if (reportId === 'quotation-register') return 'estimate_id' as keyof TableRow;
    if (reportId === 'job-production-cost' || reportId === 'estimated-vs-actual' || reportId === 'contribution-margin-per-job') return 'wo_id' as keyof TableRow;
    if (reportId === 'wip-report' || reportId === 'grc-material-consumption') return 'wo_id' as keyof TableRow;
    if (reportId === 'material-consumption-per-job') return 'item_code' as keyof TableRow;
    if (reportId === 'stock-ledger') return 'move_id' as keyof TableRow;
    if (reportId === 'minimum-stock-alert' || reportId === 'raw-material-stock') return 'item_id' as keyof TableRow;
    if (reportId === 'balance-sheet') return 'row_id' as keyof TableRow;
    if (reportId === 'trial-balance') return 'row_id' as keyof TableRow;
    if (reportId === 'profit-and-loss') return 'row_id' as keyof TableRow;
    if (reportId === 'payable-aging') return 'supplier_id' as keyof TableRow;
    if (reportId === 'receivable-aging') return 'customer_id' as keyof TableRow;
    if (reportId === 'product-profitability') return 'family' as keyof TableRow;
    return 'inquiry_id' as keyof TableRow;
}

function toMetricRowId(label: string, index: number) {
    return `metric-${index}-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'value'}`;
}

function buildMetricRows(summary: Record<string, string | number>): MetricReportRow[] {
    return Object.entries(summary).map(([metric, value], index) => ({
        row_id: toMetricRowId(metric, index),
        metric,
        value,
    }));
}

function buildSummaryReport(title: string, summary: Record<string, string | number>, note?: string): LoadedReport {
    const rows = buildMetricRows(summary);
    return {
        kind: 'table',
        rows,
        title,
        total: rows.length,
        limit: 25,
        offset: 0,
        hasMore: false,
        summary,
        note,
    };
}

function buildDefaultSummary(report: Extract<LoadedReport, { kind: 'table' }>): Record<string, string | number> {
    const start = report.total === 0 ? 0 : report.offset + 1;
    const end = Math.min(report.offset + report.rows.length, report.total);
    return {
        'Total rows': report.total,
        'Showing rows': report.total === 0 ? '0' : `${start}-${end}`,
    };
}

function getReadinessLabel(readiness?: ReportReleaseReadinessItem | null) {
    if (!readiness) return null;
    if (readiness.status === 'ready_for_release') return 'Ready for release';
    if (readiness.status === 'pending_approval') return 'Pending approval';
    return 'Setup incomplete';
}

export function ReportsDashboard() {
    const navigate = useNavigate();
    const [catalogQuery, setCatalogQuery] = useState('');
    const [navigatorOpen, setNavigatorOpen] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [filingExporting, setFilingExporting] = useState(false);
    const [vatMode, setVatMode] = useState<VatWorkspaceMode>('summary');
    const [vatPeriodPreset, setVatPeriodPreset] = useState<'latest' | 'march-may-2026' | 'custom'>('latest');
    const [vatSelectedBillIds, setVatSelectedBillIds] = useState<number[]>([]);
    const [vatAccounts, setVatAccounts] = useState<Account[]>([]);
    const [vatActionBusy, setVatActionBusy] = useState(false);
    const [vatRefreshToken, setVatRefreshToken] = useState(0);
    const [loadedReport, setLoadedReport] = useState<LoadedReport | null>(null);
    const [ledgerAccount, setLedgerAccount] = useState<Pick<Account, 'id' | 'code' | 'name' | 'type'> | null>(null);
    const [ledgerOpen, setLedgerOpen] = useState(false);
    const [releaseReadiness, setReleaseReadiness] = useState<Record<string, ReportReleaseReadinessItem>>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const deferredCatalogQuery = useDeferredValue(catalogQuery);
    const [searchParams, setSearchParams] = useSearchParams();
    const reportParam = searchParams.get('report');
    const fallbackReportId = reportDefinitions.find((item) => item.status === 'LIVE')?.id || reportDefinitions[0].id;
    const selectedReportId = reportDefinitions.some((item) => item.id === reportParam) ? String(reportParam) : fallbackReportId;
    const selectedReport = useMemo(
        () => reportDefinitions.find((item) => item.id === selectedReportId) || reportDefinitions[0],
        [selectedReportId]
    );
    const [expandedModule, setExpandedModule] = useState<ReportModule | null>(selectedReport.module);

    const { state, setFilter, setFilters, clearAll, isDirty } = useTableFilterState<Pick<ReportWorkspaceState, 'q' | 'range' | 'group_by' | 'limit' | 'offset'>>(workspaceDefaults);

    const limit = Number(state.limit || selectedReport.defaultPageSize || 25);
    const offset = Number(state.offset || 0);
    const latestVatPeriod = useMemo(() => getLatestCompletedQuarterPeriod(), []);
    const selectedDateRange = toDateRange(state.range);
    const from = selectedReport.id === 'vat-summary' && !selectedDateRange.from
        ? latestVatPeriod.from
        : selectedDateRange.from;
    const to = selectedReport.id === 'vat-summary' && !selectedDateRange.to
        ? latestVatPeriod.to
        : selectedDateRange.to;
    const vatPeriodLabel = vatPeriodPreset === 'march-may-2026'
        ? `March-May 2026: ${formatPeriodDate(from || '2026-03-01')} - ${formatPeriodDate(to || '2026-05-31')}`
        : vatPeriodPreset === 'custom'
            ? `Custom Period: ${formatPeriodDate(from || latestVatPeriod.from)} - ${formatPeriodDate(to || latestVatPeriod.to)}`
            : `Latest Quarter: ${formatPeriodDate(from || latestVatPeriod.from)} - ${formatPeriodDate(to || latestVatPeriod.to)}`;

    const catalogSearchTerm = deferredCatalogQuery.trim().toLowerCase();
    const isCatalogSearching = catalogSearchTerm.length > 0;

    const groupedDefinitions = useMemo(
        () =>
            reportModules
                .map((module) => ({
                    module,
                    items: reportDefinitions.filter((item) => item.module === module && `${item.title} ${item.description}`.toLowerCase().includes(catalogSearchTerm)),
                }))
                .filter((group) => group.items.length > 0),
        [catalogSearchTerm]
    );
    const visibleExpandedModules = useMemo(
        () => new Set<ReportModule>(isCatalogSearching ? groupedDefinitions.map((group) => group.module) : expandedModule ? [expandedModule] : []),
        [expandedModule, groupedDefinitions, isCatalogSearching]
    );
    const selectedReportIndex = useMemo(
        () => reportDefinitions.findIndex((item) => item.id === selectedReport.id),
        [selectedReport.id]
    );
    const previousReport = selectedReportIndex > 0 ? reportDefinitions[selectedReportIndex - 1] : null;
    const nextReport = selectedReportIndex >= 0 && selectedReportIndex < reportDefinitions.length - 1 ? reportDefinitions[selectedReportIndex + 1] : null;
    const selectedReadiness = selectedReport.readinessKey ? releaseReadiness[selectedReport.readinessKey] : undefined;
    const reportIsReleaseReady = Boolean(selectedReadiness?.status === 'ready_for_release');
    const reportCanLoad = selectedReport.deliveryMode === 'linked' || selectedReport.status === 'LIVE' || reportIsReleaseReady;
    const isLoadedReportCurrent = loadedReport?.title === selectedReport.title;

    const updateFilterAndResetPage = (key: 'q' | 'range' | 'group_by', value: string) => {
        setFilters({ [key]: value, offset: '0' } as any);
    };

    const applyReportSelection = (reportId: string) => {
        const nextReportDefinition = reportDefinitions.find((item) => item.id === reportId) || reportDefinitions[0];
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('report', reportId);
        nextParams.set('f_limit', String(nextReportDefinition.defaultPageSize));
        nextParams.delete('f_offset');
        setSearchParams(nextParams, { replace: true });
        setExpandedModule(nextReportDefinition.module);
        setNavigatorOpen(false);
    };

    const activeControls = useMemo(() => {
        const controls: Array<any> = [];
        if (selectedReport.filters.includes('dateRange')) {
            controls.push({
                key: 'range',
                type: 'daterange',
                label: 'Period',
                value: selectedReport.id === 'vat-summary' && !state.range
                    ? `${latestVatPeriod.from}|${latestVatPeriod.to}`
                    : state.range,
                onChange: (value: string) => {
                    if (selectedReport.id === 'vat-summary') setVatPeriodPreset('custom');
                    updateFilterAndResetPage('range', value);
                },
            });
        }
        if (selectedReport.filters.includes('asOfDate')) {
            controls.push({
                key: 'range',
                type: 'asofdate',
                label: 'Date',
                value: state.range,
                onChange: (value: string) => updateFilterAndResetPage('range', value),
            });
        }
        if (selectedReport.filters.includes('groupBy')) {
            controls.push({
                key: 'group_by',
                type: 'select',
                label: 'Group By',
                value: state.group_by,
                onChange: (value: string) => updateFilterAndResetPage('group_by', value),
                options: [
                    { label: 'Daily', value: 'daily' },
                    { label: 'Weekly', value: 'weekly' },
                ],
            });
        }
        return controls;
    }, [latestVatPeriod.from, latestVatPeriod.to, selectedReport.filters, selectedReport.id, state.group_by, state.range]);

    const canSearch = selectedReport.filters.includes('search');
    const canExport = Boolean(selectedReport.exportable && selectedReport.exportEndpoint && reportCanLoad);
    const vatPack = selectedReport.id === 'vat-summary' && loadedReport?.kind === 'table' ? loadedReport.vat : undefined;
    const isFinancialStatementReport = ['balance-sheet', 'trial-balance', 'profit-and-loss'].includes(selectedReport.id);
    const ledgerInitialFrom = selectedReport.id === 'profit-and-loss' ? from || '' : '';
    const ledgerInitialTo = selectedReport.id === 'profit-and-loss'
        ? to || ''
        : selectedReport.id === 'balance-sheet' || selectedReport.id === 'trial-balance'
            ? state.range || ''
            : '';

    useEffect(() => {
        if (!searchParams.get('report')) {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.set('report', fallbackReportId);
            setSearchParams(nextParams, { replace: true });
        }
    }, [fallbackReportId, searchParams, setSearchParams]);

    useEffect(() => {
        setExpandedModule(selectedReport.module);
    }, [selectedReport.module]);

    useEffect(() => {
        if (selectedReport.id !== 'vat-summary') return;
        let active = true;
        api.financials.listAccounts()
            .then((accounts) => {
                if (active) setVatAccounts(accounts);
            })
            .catch(() => {
                if (active) setVatAccounts([]);
            });
        return () => {
            active = false;
        };
    }, [selectedReport.id]);

    useEffect(() => {
        let active = true;
        async function loadReleaseReadiness() {
            try {
                const payload = await api.reports.getReleaseReadiness();
                if (active) {
                    setReleaseReadiness(payload.reports || {});
                }
            } catch {
                if (active) {
                    setReleaseReadiness({});
                }
            }
        }
        void loadReleaseReadiness();
        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        let active = true;

        async function loadSelectedReport() {
            setLoadedReport(null);
            if (!reportCanLoad) {
                setLoadedReport({
                    kind: 'none',
                    title: selectedReport.title,
                    note: selectedReadiness?.blocker || selectedReport.blocker || 'This report is not live yet.',
                });
                setLoading(false);
                setError(null);
                return;
            }

            if (selectedReport.deliveryMode === 'linked') {
                setLoadedReport({
                    kind: 'linked',
                    title: selectedReport.title,
                    note: 'This report remains in its live production workspace. Use the action below to open the existing screen without changing current routes.',
                    targetRoute: selectedReport.targetRoute || '/',
                });
                setLoading(false);
                setError(null);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                let nextReport: LoadedReport;
                switch (selectedReport.id) {
                    case 'enquiry-register': {
                        const payload = await api.reports.getEnquiryRegister({ from, to, q: canSearch ? state.q || undefined : undefined, limit, offset });
                        nextReport = { kind: 'table', rows: payload.items, title: selectedReport.title, total: payload.total, limit: payload.limit, offset: payload.offset, hasMore: payload.has_more };
                        break;
                    }
                    case 'sales-pipeline': {
                        const payload = await api.reports.getSalesPipeline({ from, to });
                        nextReport = buildSummaryReport(selectedReport.title, {
                            'Total Enquiries Value': formatMoney(payload.total_enquiries_value),
                            'Quotations Submitted': formatMoney(payload.quotations_submitted_value),
                            'Orders Expected': formatMoney(payload.orders_expected_value),
                        }, payload.basis);
                        break;
                    }
                    case 'conversion-ratio': {
                        const payload = await api.reports.getEnquiryConversion({ from, to });
                        nextReport = buildSummaryReport(selectedReport.title, {
                            'Enquiries Received': payload.enquiries_received,
                            'Quotations Issued': payload.quotations_issued,
                            'Orders Won': payload.orders_won,
                            'Conversion %': `${payload.conversion_percent.toFixed(2)}%`,
                        });
                        break;
                    }
                    case 'quotation-register': {
                        const payload = await api.reports.getQuotationRegister({ from, to, q: canSearch ? state.q || undefined : undefined, limit, offset });
                        nextReport = { kind: 'table', rows: payload.items, title: selectedReport.title, total: payload.total, limit: payload.limit, offset: payload.offset, hasMore: payload.has_more };
                        break;
                    }
                    case 'estimated-cost-breakdown': {
                        const payload = await api.reports.getEstimatedCostBreakdown({ from, to, q: canSearch ? state.q || undefined : undefined, limit, offset });
                        nextReport = { kind: 'table', rows: payload.items, title: selectedReport.title, total: payload.total, limit: payload.limit, offset: payload.offset, hasMore: payload.has_more };
                        break;
                    }
                    case 'production-output': {
                        const payload = await api.reports.getProductionOutput({ from, to, group_by: state.group_by || 'daily', q: canSearch ? state.q || undefined : undefined, limit, offset });
                        nextReport = { kind: 'table', rows: payload.items, title: selectedReport.title, total: payload.total, limit: payload.limit, offset: payload.offset, hasMore: payload.has_more };
                        break;
                    }
                    case 'raw-material-stock': {
                        const payload = await api.reports.getRawMaterialStock({ q: canSearch ? state.q || undefined : undefined, limit, offset });
                        nextReport = { kind: 'table', rows: payload.items, title: selectedReport.title, total: payload.total, limit: payload.limit, offset: payload.offset, hasMore: payload.has_more };
                        break;
                    }
                    case 'stock-ledger': {
                        const payload = await api.reports.getStockLedger({ from, to, q: canSearch ? state.q || undefined : undefined, limit, offset });
                        nextReport = {
                            kind: 'table',
                            rows: payload.items,
                            title: selectedReport.title,
                            total: payload.total,
                            limit: payload.limit,
                            offset: payload.offset,
                            hasMore: payload.has_more,
                            note: 'Read-only ledger view in the Reports hub. The production Inventory ledger remains unchanged.',
                        };
                        break;
                    }
                    case 'minimum-stock-alert': {
                        const payload = await api.reports.getMinimumStockAlert({ q: canSearch ? state.q || undefined : undefined, limit, offset });
                        nextReport = { kind: 'table', rows: payload.items, title: selectedReport.title, total: payload.total, limit: payload.limit, offset: payload.offset, hasMore: payload.has_more };
                        break;
                    }
                    case 'receivable-aging': {
                        const payload: ReceivablesAgingResponse = await api.reports.getReceivablesAging({ as_of: state.range || undefined, q: canSearch ? state.q || undefined : undefined, limit, offset });
                        nextReport = { kind: 'table', rows: payload.items, title: selectedReport.title, total: payload.total, limit: payload.limit, offset: payload.offset, hasMore: payload.has_more };
                        break;
                    }
                    case 'payable-aging': {
                        const payload: PayablesAgingResponse = await api.reports.getPayablesAging({ as_of: state.range || undefined, q: canSearch ? state.q || undefined : undefined, limit, offset });
                        nextReport = { kind: 'table', rows: payload.items, title: selectedReport.title, total: payload.total, limit: payload.limit, offset: payload.offset, hasMore: payload.has_more };
                        break;
                    }
                    case 'vat-summary': {
                        const payload = await api.reports.getVATSummary({ from, to, q: canSearch ? state.q || undefined : undefined, limit, offset });
                        const summary = {
                            'Taxable Sales': formatMoney(payload.taxable_sales),
                            'Output VAT': formatMoney(payload.output_vat),
                            'Taxable Purchases': formatMoney(payload.taxable_purchases),
                            'Input VAT': formatMoney(payload.input_vat),
                            'Net Payable': formatMoney(payload.net_payable),
                        };
                        nextReport = {
                            kind: 'table',
                            rows: payload.items,
                            title: selectedReport.title,
                            total: payload.total,
                            limit: payload.limit,
                            offset: payload.offset,
                            hasMore: payload.has_more,
                            summary,
                            note: payload.basis,
                            vat: payload,
                        };
                        break;
                    }
                    case 'balance-sheet': {
                        const payload = await api.reports.getBalanceSheet({ to: state.range || undefined, limit, offset });
                        const sectionTotals = payload.items.filter((row) => row.row_type === 'section_total');
                        nextReport = {
                            kind: 'table',
                            rows: payload.items,
                            title: selectedReport.title,
                            total: payload.total,
                            limit: payload.limit,
                            offset: payload.offset,
                            hasMore: payload.has_more,
                            note: payload.basis,
                            summary: sectionTotals.length
                                ? Object.fromEntries(sectionTotals.map((row) => [row.section, formatMoney(row.balance)]))
                                : undefined,
                        };
                        break;
                    }
                    case 'trial-balance': {
                        const payload: TrialBalanceReportResponse = await api.reports.getTrialBalance({ to: state.range || undefined, q: canSearch ? state.q || undefined : undefined, limit, offset });
                        nextReport = {
                            kind: 'table',
                            rows: payload.items,
                            title: selectedReport.title,
                            total: payload.total,
                            limit: payload.limit,
                            offset: payload.offset,
                            hasMore: payload.has_more,
                            note: `${payload.basis} Total debit: ${formatMoney(payload.total_debit)}. Total credit: ${formatMoney(payload.total_credit)}. Difference: ${formatMoney(payload.difference)}.`,
                            summary: {
                                'Total Debit': formatMoney(payload.total_debit),
                                'Total Credit': formatMoney(payload.total_credit),
                                Difference: formatMoney(payload.difference),
                            },
                        };
                        break;
                    }
                    case 'cash-flow': {
                        const payload = await api.reports.getCashFlow({ from, to });
                        nextReport = buildSummaryReport(selectedReport.title, {
                            'Opening Balance': formatMoney(payload.opening_balance),
                            'Cash In': formatMoney(payload.cash_in),
                            'Cash Out': formatMoney(payload.cash_out),
                            'Closing Balance': formatMoney(payload.closing_balance),
                        }, payload.basis);
                        break;
                    }
                    case 'wip-report': {
                        const payload = await api.reports.getWipReport({ from, to, limit, offset });
                        nextReport = {
                            kind: 'table',
                            rows: payload.items,
                            title: selectedReport.title,
                            total: payload.total,
                            limit: payload.limit,
                            offset: payload.offset,
                            hasMore: payload.has_more,
                            note: `${payload.basis} Jobs in progress: ${payload.summary.jobs_in_progress}. Total WIP value: ${formatMoney(payload.summary.total_wip_value)}.`,
                            summary: {
                                'Jobs In Progress': payload.summary.jobs_in_progress,
                                'Total WIP Value': formatMoney(payload.summary.total_wip_value),
                            },
                        };
                        break;
                    }
                    case 'break-even': {
                        const payload: BreakEvenReport = await api.reports.getBreakEven({ from, to });
                        nextReport = buildSummaryReport(selectedReport.title, {
                            'Net Sales': formatMoney(payload.net_sales),
                            'Variable Cost': formatMoney(payload.variable_cost),
                            'Fixed Cost': formatMoney(payload.fixed_cost),
                            'Contribution Margin': formatMoney(payload.contribution_margin),
                            'CM Ratio': `${payload.contribution_margin_ratio.toFixed(2)}%`,
                            'Break-Even Sales': formatMoney(payload.break_even_sales),
                        }, payload.basis);
                        break;
                    }
                    case 'product-profitability': {
                        const payload = await api.reports.getProductProfitability({ from, to, limit, offset });
                        nextReport = {
                            kind: 'table',
                            rows: payload.items,
                            title: selectedReport.title,
                            total: payload.total,
                            limit: payload.limit,
                            offset: payload.offset,
                            hasMore: payload.has_more,
                            note: payload.basis,
                        };
                        break;
                    }
                    case 'grc-material-consumption': {
                        const payload = await api.reports.getGrcMaterialConsumption({ from, to, limit, offset });
                        nextReport = {
                            kind: 'table',
                            rows: payload.items,
                            title: selectedReport.title,
                            total: payload.total,
                            limit: payload.limit,
                            offset: payload.offset,
                            hasMore: payload.has_more,
                            note: payload.basis,
                        };
                        break;
                    }
                    case 'profit-and-loss': {
                        const payload = await api.reports.getProfitAndLoss({ from, to, limit, offset });
                        const pnl = payload as ProfitAndLossReport;
                        const rows = pnl.items || pnl.statement || [];
                        nextReport = {
                            kind: 'table',
                            rows,
                            title: selectedReport.title,
                            total: pnl.total ?? rows.length,
                            limit: pnl.limit ?? limit,
                            offset: pnl.offset ?? offset,
                            hasMore: Boolean(pnl.has_more),
                            note: `${pnl.basis || 'Posted-journal income and expense breakdown.'} Total income: ${formatMoney(pnl.total_income)}. Total expense: ${formatMoney(pnl.total_expense)}. Net profit: ${formatMoney(pnl.net_profit)}.`,
                            summary: {
                                'Total Income': formatMoney(pnl.total_income),
                                'Total Expense': formatMoney(pnl.total_expense),
                                'Net Profit': formatMoney(pnl.net_profit),
                            },
                        };
                        break;
                    }
                    case 'estimated-vs-actual': {
                        const payload = await api.reports.getEstimatedVsActual({ q: canSearch ? state.q || undefined : undefined, limit, offset });
                        nextReport = { kind: 'table', rows: payload.items, title: selectedReport.title, total: payload.total, limit: payload.limit, offset: payload.offset, hasMore: payload.has_more };
                        break;
                    }
                    case 'job-production-cost': {
                        const payload = await api.reports.getJobProductionCosts({ from, to, q: canSearch ? state.q || undefined : undefined, limit, offset });
                        nextReport = { kind: 'table', rows: payload.items, title: selectedReport.title, total: payload.total, limit: payload.limit, offset: payload.offset, hasMore: payload.has_more };
                        break;
                    }
                    case 'material-consumption-per-job': {
                        const payload = await api.reports.getMaterialConsumptionPerJob({ from, to, q: canSearch ? state.q || undefined : undefined, limit, offset });
                        nextReport = { kind: 'table', rows: payload.items, title: selectedReport.title, total: payload.total, limit: payload.limit, offset: payload.offset, hasMore: payload.has_more };
                        break;
                    }
                    case 'contribution-margin-per-job': {
                        const payload = await api.reports.getContributionMarginPerJob({ q: canSearch ? state.q || undefined : undefined, limit, offset });
                        nextReport = { kind: 'table', rows: payload.items, title: selectedReport.title, total: payload.total, limit: payload.limit, offset: payload.offset, hasMore: payload.has_more };
                        break;
                    }
                    case 'owner-dashboard': {
                        const payload = await api.reports.getOwnerDashboard();
                        const owner = payload as OwnerDashboardReport;
                        nextReport = buildSummaryReport(selectedReport.title, {
                            'Total Sales MTD': formatMoney(owner.total_sales_mtd),
                            'Outstanding Receivables': formatMoney(owner.outstanding_receivables),
                            Payables: formatMoney(owner.payables),
                            'Bank Balance': formatMoney(owner.bank_balance),
                            'Production Cost To Date': formatMoney(owner.production_cost_to_date),
                            'Gross Profit %': `${owner.gross_profit_percent.toFixed(2)}%`,
                        }, owner.basis);
                        break;
                    }
                    default:
                        nextReport = { kind: 'none', title: selectedReport.title, note: 'No preview configured yet.' };
                }
                if (nextReport.kind === 'table' && !nextReport.summary) {
                    nextReport = { ...nextReport, summary: buildDefaultSummary(nextReport) };
                }
                if (active) setLoadedReport(nextReport);
            } catch (err) {
                if (!active) return;
                setLoadedReport(null);
                setError(err instanceof Error ? err.message : 'Failed to load report');
            } finally {
                if (active) setLoading(false);
            }
        }

        void loadSelectedReport();
        return () => {
            active = false;
        };
    }, [selectedReport, selectedReadiness, reportCanLoad, state.group_by, state.limit, state.offset, state.q, state.range, canSearch, from, limit, offset, to, vatRefreshToken]);

    const handleExport = async () => {
        if (!canExport || !selectedReport.exportEndpoint) return;
        const exportFilters: Record<string, string | number | undefined> = {};
        if (selectedReport.filters.includes('search') && state.q) exportFilters.q = state.q;
        if (selectedReport.filters.includes('dateRange')) {
            if (from) exportFilters.from = from;
            if (to) exportFilters.to = to;
        }
        if (selectedReport.filters.includes('groupBy')) {
            exportFilters.group_by = state.group_by || 'daily';
        }
        try {
            setExporting(true);
            await api.reports.exportCsv(selectedReport.exportEndpoint, exportFilters, `${selectedReport.id}.csv`);
            toast.success(`${selectedReport.title} CSV export started`);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to export CSV');
        } finally {
            setExporting(false);
        }
    };

    const handleFilingExport = async () => {
        if (selectedReport.id !== 'vat-summary' || loadedReport?.kind !== 'table' || loadedReport.vat?.readiness.status !== 'ready') return;
        const exportFilters: Record<string, string | undefined> = {};
        if (state.q) exportFilters.q = state.q;
        if (from) exportFilters.from = from;
        if (to) exportFilters.to = to;
        try {
            setFilingExporting(true);
            await api.reports.exportCsv('/reports/vat-summary/filing-export.csv', exportFilters, 'vat-filing-ready.csv');
            toast.success('VAT filing-ready CSV export started');
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to export filing CSV');
        } finally {
            setFilingExporting(false);
        }
    };

    const applyVatPeriod = (preset: 'latest' | 'march-may-2026') => {
        const period = preset === 'latest'
            ? latestVatPeriod
            : { from: '2026-03-01', to: '2026-05-31' };
        setVatPeriodPreset(preset);
        setFilters({ range: `${period.from}|${period.to}`, offset: '0' } as any);
    };

    const refreshVatReport = () => {
        setVatSelectedBillIds([]);
        setVatRefreshToken((value) => value + 1);
    };

    const handleVatBulkReview = async (recoveryPercent: number, accountId?: number | null) => {
        if (vatSelectedBillIds.length === 0) {
            toast.error('Select at least one supplier bill.');
            return;
        }
        if (!Number.isFinite(recoveryPercent) || recoveryPercent < 0 || recoveryPercent > 100) {
            toast.error('Recovery percentage must be between 0 and 100.');
            return;
        }
        if (recoveryPercent < 100 && !accountId) {
            toast.error('Select an Asset or Expense account for non-recoverable VAT.');
            return;
        }
        try {
            setVatActionBusy(true);
            await api.reports.bulkReviewVatRecovery({
                bill_ids: vatSelectedBillIds,
                recovery_percent: recoveryPercent,
                vat_treatment: 'STANDARD',
                nonrecoverable_vat_account_id: accountId || null,
            });
            toast.success(`${vatSelectedBillIds.length} supplier bill(s) reviewed`);
            refreshVatReport();
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to review Input VAT');
        } finally {
            setVatActionBusy(false);
        }
    };

    const handleVatSupplierTrnUpdate = async (supplierId: number, trn: string) => {
        try {
            setVatActionBusy(true);
            await api.reports.updateVatSupplierTrn(supplierId, trn);
            toast.success('Supplier TRN updated');
            refreshVatReport();
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to update supplier TRN');
        } finally {
            setVatActionBusy(false);
        }
    };

    const handleVatCustomerEmirateUpdate = async (customerId: number, emirate: string) => {
        try {
            setVatActionBusy(true);
            await api.reports.updateVatCustomerEmirate(customerId, emirate);
            toast.success('Customer emirate updated');
            refreshVatReport();
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to update customer emirate');
        } finally {
            setVatActionBusy(false);
        }
    };

    const handleCatalogQueryChange = (value: string) => {
        setCatalogQuery(value);
        if (!value.trim()) {
            setExpandedModule(selectedReport.module);
        }
    };

    const toggleModule = (module: ReportModule) => {
        if (isCatalogSearching) return;
        setExpandedModule((current) => (current === module ? null : module));
    };

    const handleLedgerOpen = (account: Pick<Account, 'id' | 'code' | 'name' | 'type'>) => {
        setLedgerAccount(account);
        setLedgerOpen(true);
    };

    const navigator = (
        <div className="flex h-full min-h-0 flex-col gap-4">
            <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-2">
                    <FolderKanban className="h-4 w-4 text-slate-500" />
                    <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Report Catalog</h2>
                </div>
                <p className="mt-2 text-xs leading-5 text-slate-500">Compact navigator with module accordions and quick report switching.</p>
                <div className="relative mt-4">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input
                        value={catalogQuery}
                        onChange={(event) => handleCatalogQueryChange(event.target.value)}
                        placeholder="Find a report..."
                        className="pl-9"
                    />
                </div>
            </div>

            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm">
                <div className="space-y-3">
                    {groupedDefinitions.length === 0 && (
                        <div className="rounded-[22px] border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                            No reports match this catalog search.
                        </div>
                    )}
                    {groupedDefinitions.map((group) => {
                        const isExpanded = visibleExpandedModules.has(group.module);
                        const regionId = `report-module-${group.module.toLowerCase()}`;
                        return (
                            <div key={group.module} className="overflow-hidden rounded-[24px] border border-slate-200 bg-slate-50/70">
                                <button
                                    type="button"
                                    onClick={() => toggleModule(group.module)}
                                    aria-expanded={isExpanded}
                                    aria-controls={regionId}
                                    className={cn(
                                        'flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors',
                                        isExpanded ? 'bg-white' : 'hover:bg-white/70',
                                        isCatalogSearching && 'cursor-default'
                                    )}
                                >
                                    <div>
                                        <div className="text-sm font-semibold text-slate-900">{group.module}</div>
                                        <div className="mt-1 text-[11px] uppercase tracking-[0.16em] text-slate-500">{group.items.length} report{group.items.length === 1 ? '' : 's'}</div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                                            {group.items.filter((item) => item.status === 'LIVE').length} live
                                        </span>
                                        {isExpanded ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
                                    </div>
                                </button>
                                {isExpanded && (
                                    <div id={regionId} className="space-y-2 border-t border-slate-200 bg-white p-3">
                                        {group.items.map((report: ReportDefinition) => (
                                            <button
                                                key={report.id}
                                                type="button"
                                                onClick={() => startTransition(() => applyReportSelection(report.id))}
                                                className={cn(
                                                    'w-full rounded-2xl border px-4 py-3 text-left transition-all',
                                                    report.id === selectedReportId
                                                        ? 'border-slate-900 bg-slate-900 text-white shadow-lg shadow-slate-900/10'
                                                        : 'border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-white'
                                                )}
                                            >
                                                {report.readinessKey && releaseReadiness[report.readinessKey] && (
                                                    <div className={cn('mb-3 inline-flex rounded-full border px-2 py-1 text-[10px] font-semibold uppercase tracking-wide', report.id === selectedReportId ? 'border-white/20 bg-white/10 text-white' : readinessBadgeClass[releaseReadiness[report.readinessKey].status])}>
                                                        {getReadinessLabel(releaseReadiness[report.readinessKey])}
                                                    </div>
                                                )}
                                                <div className="flex items-start justify-between gap-3">
                                                    <div className="min-w-0">
                                                        <div className="text-sm font-semibold">{report.title}</div>
                                                        <div className={cn('mt-1 text-xs leading-5', report.id === selectedReportId ? 'text-slate-300' : 'text-slate-600')}>
                                                            {report.description}
                                                        </div>
                                                    </div>
                                                    <span className={cn('inline-flex rounded-full border px-2 py-1 text-[10px] font-semibold uppercase tracking-wide', report.id === selectedReportId ? 'border-white/20 bg-white/10 text-white' : badgeClass[report.status])}>
                                                        {report.status === 'IN_IMPLEMENTATION' ? 'In implementation' : report.status === 'BLOCKED' ? 'Blocked' : 'Live'}
                                                    </span>
                                                </div>
                                                {report.blocker && (
                                                    <div className={cn('mt-3 flex items-start gap-2 rounded-xl border px-3 py-2 text-[11px]', report.id === selectedReportId ? 'border-white/10 bg-white/10 text-slate-100' : 'border-rose-200 bg-rose-50 text-rose-700')}>
                                                        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                                        <span>{report.blocker}</span>
                                                    </div>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-6 pb-8">
            <div className="space-y-4">
                <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <h1 className="text-3xl font-bold tracking-tight text-foreground">Reports Suite</h1>
                            <PageHelp title="Reports Suite" description="Client-requested reports tracked against the metric audit.">
                                <p>The navigator keeps every report visible while the workspace stays focused on the active report.</p>
                            </PageHelp>
                        </div>
                        <p className="max-w-3xl text-sm text-muted-foreground">
                            Metric availability stays driven by the audit. The catalog is compact and searchable so the workspace stays primary.
                        </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        <Button variant="outline" className="xl:hidden" onClick={() => setNavigatorOpen(true)}>
                            <PanelLeft className="mr-2 h-4 w-4" />
                            Browse Reports
                        </Button>
                        <Button variant="outline" onClick={() => window.location.reload()}>
                            <RefreshCw className="mr-2 h-4 w-4" />
                            Refresh
                        </Button>
                        <Button
                            onClick={() => void handleExport()}
                            disabled={!canExport || exporting}
                            aria-label={selectedReport.id === 'vat-summary' ? 'Download Review CSV' : 'Export CSV'}
                        >
                            <Download className="mr-2 h-4 w-4" />
                            {exporting ? 'Exporting...' : selectedReport.id === 'vat-summary' ? 'Download Review CSV' : 'Export CSV'}
                        </Button>
                        {selectedReport.id === 'vat-summary' && (
                            <Button
                                variant="outline"
                                onClick={() => void handleFilingExport()}
                                disabled={vatPack?.readiness.status !== 'ready' || filingExporting}
                                aria-label="Download Filing CSV"
                            >
                                <Download className="mr-2 h-4 w-4" />
                                {filingExporting ? 'Exporting...' : 'Download Filing CSV'}
                            </Button>
                        )}
                    </div>
                </div>

            </div>

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-[320px,minmax(0,1fr)]">
                <aside className="hidden xl:block">
                    <div className="sticky top-6 h-[calc(100vh-5rem)] overflow-hidden">{navigator}</div>
                </aside>

                <section className="min-w-0 rounded-[30px] border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
                    <div className="-mx-5 -mt-5 border-b border-slate-100 bg-white px-5 pb-4 pt-5 sm:-mx-6 sm:-mt-6 sm:px-6 sm:pt-6">
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <BarChart3 className="h-5 w-5 text-slate-600" />
                                    <h2 className="text-2xl font-semibold text-slate-900">{selectedReport.title}</h2>
                                </div>
                                <p className="max-w-2xl text-sm text-slate-600">{selectedReport.description}</p>
                                <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                                    <span className={cn('inline-flex rounded-full border px-3 py-1 font-semibold uppercase tracking-wide', badgeClass[selectedReport.status])}>
                                        {selectedReport.status === 'IN_IMPLEMENTATION' ? 'In Implementation' : selectedReport.status === 'BLOCKED' ? 'Blocked by Metric/Policy' : 'Live'}
                                    </span>
                                    {selectedReadiness && (
                                        <span className={cn('inline-flex rounded-full border px-3 py-1 font-semibold uppercase tracking-wide', readinessBadgeClass[selectedReadiness.status])}>
                                            {getReadinessLabel(selectedReadiness)}
                                        </span>
                                    )}
                                    {selectedReport.deliveryMode === 'linked' ? (
                                        <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1">
                                            Linked route: {selectedReport.targetRoute}
                                        </span>
                                    ) : selectedReport.jsonEndpoint ? (
                                        <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1">
                                            Endpoint: {selectedReport.jsonEndpoint}
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                            <div className="flex flex-col gap-3 lg:items-end">
                                <div className="flex flex-wrap gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => previousReport && applyReportSelection(previousReport.id)}
                                        disabled={!previousReport}
                                    >
                                        <ArrowLeft className="mr-2 h-4 w-4" />
                                        Previous Report
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => nextReport && applyReportSelection(nextReport.id)}
                                        disabled={!nextReport}
                                    >
                                        Next Report
                                        <ArrowRight className="ml-2 h-4 w-4" />
                                    </Button>
                                </div>
                                {(selectedReadiness?.blocker || selectedReport.blocker) && (
                                    <div className="flex max-w-md items-start gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                                        <Lock className="mt-0.5 h-4 w-4 shrink-0" />
                                        <span>{selectedReadiness?.blocker || selectedReport.blocker}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="mt-5 space-y-4">
                        <TableFilterBar
                            searchValue={canSearch ? state.q : ''}
                            onSearchChange={(value) => canSearch && updateFilterAndResetPage('q', value)}
                            searchPlaceholder={canSearch ? 'Search active report...' : 'Search not applicable for this report'}
                            controls={activeControls}
                            isDirty={isDirty}
                            onClear={() => {
                                setFilters({ ...workspaceDefaults, limit: String(selectedReport.defaultPageSize), offset: '0' } as any);
                            }}
                            rightSlot={<span>{selectedReport.supportsPagination ? 'Server-side paging enabled' : 'Metric table'}{canExport ? ' • CSV export available' : ' • Export disabled'}</span>}
                        />

                        {selectedReport.id === 'vat-summary' && (
                            <div className="flex flex-col gap-3 border border-slate-200 bg-slate-50 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
                                <div>
                                    <div className="text-sm font-semibold text-slate-900">{vatPeriodLabel}</div>
                                    <div className="mt-1 text-xs text-slate-500">All VAT totals, blockers, reviews, and exports use this period.</div>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <Button
                                        size="sm"
                                        variant={vatPeriodPreset === 'latest' ? 'default' : 'outline'}
                                        onClick={() => applyVatPeriod('latest')}
                                    >
                                        Latest Quarter
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant={vatPeriodPreset === 'march-may-2026' ? 'default' : 'outline'}
                                        onClick={() => applyVatPeriod('march-may-2026')}
                                    >
                                        March-May 2026
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => applyVatPeriod('latest')}
                                    >
                                        Reset Period
                                    </Button>
                                </div>
                            </div>
                        )}

                        {loading && <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">Loading report workspace...</div>}
                        {!loading && error && <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-8 text-sm text-rose-700">{error}</div>}
                        {!loading && !error && isLoadedReportCurrent && loadedReport?.kind === 'none' && <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-8 text-sm text-amber-800">{loadedReport.note}</div>}
                        {!loading && !error && isLoadedReportCurrent && loadedReport?.kind === 'linked' && (
                            <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-5">
                                <div className="space-y-3">
                                    <div className="text-sm text-slate-600">{loadedReport.note}</div>
                                    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                                        Current route: {loadedReport.targetRoute}
                                    </div>
                                    <Button onClick={() => navigate(loadedReport.targetRoute)}>
                                        Open Current Production Screen
                                    </Button>
                                </div>
                            </div>
                        )}
                        {!loading && !error && isLoadedReportCurrent && vatPack && (
                            <VatFilingWorkbench
                                vat={vatPack}
                                mode={vatMode}
                                accounts={vatAccounts}
                                selectedBillIds={vatSelectedBillIds}
                                busy={vatActionBusy}
                                onModeChange={setVatMode}
                                onSelectionChange={setVatSelectedBillIds}
                                onBulkReview={handleVatBulkReview}
                                onSupplierTrnUpdate={handleVatSupplierTrnUpdate}
                                onCustomerEmirateUpdate={handleVatCustomerEmirateUpdate}
                            />
                        )}
                        {!loading && !error && isLoadedReportCurrent && loadedReport?.kind === 'table' && loadedReport.summary && selectedReport.id !== 'vat-summary' && (
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-4">
                                {Object.entries(loadedReport.summary).map(([label, value]) => (
                                    <div key={label} className="rounded-[24px] border border-slate-200 bg-slate-50 p-5">
                                        <div className="text-sm font-medium text-slate-500">{label}</div>
                                        <div className="mt-3 text-3xl font-bold text-slate-900">{value}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {!loading && !error && isLoadedReportCurrent && loadedReport?.kind === 'table' && isFinancialStatementReport && (
                            <div className="space-y-3">
                                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                                    Click account rows to inspect the supporting journal ledger without leaving the report workspace.
                                </div>
                                <FinancialStatementTable
                                    mode={selectedReport.id as 'balance-sheet' | 'trial-balance' | 'profit-and-loss'}
                                    rows={loadedReport.rows as Array<BalanceSheetRow | ProfitAndLossReportLine | TrialBalanceReportRow>}
                                    total={loadedReport.total}
                                    limit={loadedReport.limit}
                                    offset={loadedReport.offset}
                                    onPageChange={(nextOffset) => setFilter('offset', String(nextOffset))}
                                    onPageSizeChange={(nextLimit) => {
                                        setFilter('limit', String(nextLimit));
                                        setFilter('offset', '0');
                                    }}
                                    onAccountClick={handleLedgerOpen}
                                />
                            </div>
                        )}
                        {!loading && !error && isLoadedReportCurrent && loadedReport?.kind === 'table' && !isFinancialStatementReport && (selectedReport.id !== 'vat-summary' || vatMode !== 'review') && (
                            <div className="space-y-3">
                                {selectedReport.id === 'vat-summary' && (
                                    <h3 className="text-base font-semibold text-slate-900">Document VAT Lines</h3>
                                )}
                                <PagedReportTable<TableRow>
                                    data={loadedReport.rows}
                                    columns={getTableColumnsForReport(selectedReport.id) as ReportColumn[]}
                                    keyField={getKeyField(selectedReport.id)}
                                    total={loadedReport.total}
                                    limit={loadedReport.limit}
                                    offset={loadedReport.offset}
                                    onPageChange={(nextOffset) => setFilter('offset', String(nextOffset))}
                                    onPageSizeChange={(nextLimit) => {
                                        setFilter('limit', String(nextLimit));
                                        setFilter('offset', '0');
                                    }}
                                    emptyMessage="No rows match the current filters."
                                />
                            </div>
                        )}
                        {!loading && !error && isLoadedReportCurrent && loadedReport?.kind === 'table' && loadedReport.note && (
                            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">{loadedReport.note}</div>
                        )}
                    </div>
                </section>
            </div>

            <Dialog open={navigatorOpen} onOpenChange={setNavigatorOpen}>
                <DialogContent className="max-w-2xl p-0">
                    <DialogHeader className="border-b border-slate-200 px-6 py-5">
                        <DialogTitle>Browse reports</DialogTitle>
                        <DialogDescription>Pick a report and the workspace will switch immediately.</DialogDescription>
                    </DialogHeader>
                    <div className="max-h-[75vh] overflow-y-auto p-4">{navigator}</div>
                </DialogContent>
            </Dialog>

            <AccountLedgerDialog
                open={ledgerOpen}
                onOpenChange={setLedgerOpen}
                account={ledgerAccount}
                initialFrom={ledgerInitialFrom}
                initialTo={ledgerInitialTo}
            />
        </div>
    );
}
